import apiClient from './client';
import type {
  SurveySummary, SurveyDetail, SurveyResults,
  SurveyRespondContext, ExtractedEmails, AiMapAnswersResponse,
} from '../../features/survey/types';

const BASE = '/api/surveys';

export const surveyApi = {
  listSurveys: (status?: string) =>
    apiClient.get<SurveySummary[]>(BASE, {
      params: status ? { status } : undefined,
    }),

  createSurvey: (data: {
    title: string;
    questions: { question_text: string; position: number }[];
    recipient_emails?: string[];
    deadline?: string | null;
  }) =>
    apiClient.post<SurveyDetail>(BASE, data),

  getSurvey: (id: string) =>
    apiClient.get<SurveyDetail>(`${BASE}/${id}`),

  updateSurvey: (id: string, data: {
    title?: string;
    questions?: { question_text: string; position: number }[];
    recipient_emails?: string[];
    deadline?: string | null;
  }) =>
    apiClient.patch<SurveyDetail>(`${BASE}/${id}`, data),

  deleteSurvey: (id: string) =>
    apiClient.delete(`${BASE}/${id}`),

  sendSurvey: (id: string) =>
    apiClient.post<SurveyDetail>(`${BASE}/${id}/send`),

  stopSurvey: (id: string) =>
    apiClient.post<SurveyDetail>(`${BASE}/${id}/stop`),

  uploadRecipients: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ExtractedEmails>(`${BASE}/${id}/upload-recipients`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getResults: (id: string) =>
    apiClient.get<SurveyResults>(`${BASE}/${id}/results`),

  getAiSummary: (id: string) =>
    apiClient.get<{ survey_id: string; ai_summary: string; generated_at: string }>(
      `${BASE}/${id}/ai-summary`,
    ),

  regenerateAiSummary: (id: string) =>
    apiClient.post<{ survey_id: string; ai_summary: string; generated_at: string }>(
      `${BASE}/${id}/regenerate-ai-summary`,
    ),

  getExportPdfUrl: (id: string, lang?: string) =>
    `${apiClient.defaults.baseURL}${BASE}/${id}/export-pdf${lang ? `?lang=${lang}` : ''}`,

  togglePublicLink: (id: string) =>
    apiClient.post<{ public_link_enabled: boolean; public_link_token: string | null }>(
      `${BASE}/${id}/toggle-public-link`,
    ),

  // Per-recipient invite endpoints
  getRespondContext: (token: string) =>
    apiClient.get<SurveyRespondContext>(`${BASE}/respond/${token}`),

  submitResponse: (token: string, data: { answers: { question_id: string; answer_text: string }[]; name?: string }) =>
    apiClient.post(`${BASE}/respond/${token}`, data),

  // Public link endpoints
  getPublicContext: (token: string) =>
    apiClient.get<SurveyRespondContext>(`${BASE}/public/${token}`),

  submitPublicResponse: (token: string, data: { name: string; email: string; answers: { question_id: string; answer_text: string }[] }) =>
    apiClient.post(`${BASE}/public/${token}`, data),

  mapAnswers: (id: string, payload: { content?: string; file?: File }) => {
    const formData = new FormData();
    if (payload.content) formData.append('content', payload.content);
    if (payload.file) formData.append('file', payload.file);
    return apiClient.post<AiMapAnswersResponse>(`${BASE}/${id}/map-answers`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  storeManualResponse: (id: string, data: { email: string; name: string; answers: { question_id: string; answer_text: string }[] }) =>
    apiClient.post<{ ok: boolean }>(`${BASE}/${id}/store-manual-response`, data),
};
